import React, { useState, useEffect, useRef, useCallback } from 'react';

const B = {
  bg:           '#faf7f2',
  surface:      '#ffffff',
  border:       'rgba(60,45,30,0.10)',
  borderStrong: 'rgba(60,45,30,0.16)',
  text:         '#2a241d',
  muted:        '#6b6055',
  faint:        '#9a8f82',
  green:        '#7BC19E',
  greenDark:    '#5fa882',
  greenSoft:    '#eef6f1',
  charcoal:     '#3A3D3C',
  blue:         '#5a7abf',
  blueDark:     '#3d5c9e',
  amber:        '#f0c74e',
  amberDark:    '#c9a12e',
};

const font = "'Nunito', -apple-system, sans-serif";
const mono = "'JetBrains Mono', 'Fira Mono', monospace";

// ── Network topology ──────────────────────────────────────────────────────────

// Canvas internal resolution — node coordinates must fit within CANVAS_W × 240
const CANVAS_W = 680;

// SW1 → R0 (single upstream router — LAN default gateway)
// R0/R5 are WAN edge routers; R1-R4 are the cross-connected WAN cloud
const NODES = {
  S:   { x: 40,  y: 120, role: 'endpoint', label: 'S',   name: 'Sender'   },
  SW1: { x: 112, y: 120, role: 'switch',   label: 'SW1'                   },
  R0:  { x: 190, y: 120, role: 'router',   label: 'R0'                    },
  R1:  { x: 290, y: 55,  role: 'router',   label: 'R1'                    },
  R2:  { x: 290, y: 185, role: 'router',   label: 'R2'                    },
  R3:  { x: 400, y: 55,  role: 'router',   label: 'R3'                    },
  R4:  { x: 400, y: 185, role: 'router',   label: 'R4'                    },
  R5:  { x: 490, y: 120, role: 'router',   label: 'R5'                    },
  SW2: { x: 565, y: 120, role: 'switch',   label: 'SW2'                   },
  D:   { x: 640, y: 120, role: 'endpoint', label: 'D',   name: 'Receiver' },
};

const EDGES = [
  ['S', 'SW1'], ['SW1', 'R0'],
  ['R0', 'R1'], ['R0', 'R2'],
  ['R1', 'R3'], ['R1', 'R4'],
  ['R2', 'R3'], ['R2', 'R4'],
  ['R3', 'R5'], ['R4', 'R5'],
  ['R5', 'SW2'], ['SW2', 'D'],
];

// Packet payload is fixed at 2 chars — in real networks this is governed by the MTU
const PKT_SIZE = 2;

// Four distinct routes through the WAN cloud — speed multipliers create out-of-order arrival
// Route 0: fast (×1.3), Route 1: slow (×0.65), Route 2: medium (×0.95), Route 3: sluggish (×0.8)
const ROUTES = [
  ['S', 'SW1', 'R0', 'R1', 'R3', 'R5', 'SW2', 'D'],
  ['S', 'SW1', 'R0', 'R2', 'R4', 'R5', 'SW2', 'D'],
  ['S', 'SW1', 'R0', 'R1', 'R4', 'R5', 'SW2', 'D'],
  ['S', 'SW1', 'R0', 'R2', 'R3', 'R5', 'SW2', 'D'],
];
const ROUTE_SPEED_MULT = [1.3, 0.65, 0.95, 0.8];

const MESSAGES = ['HELLO', 'IB CS ROCKS', 'NETWORK'];

// ── Canvas drawing ────────────────────────────────────────────────────────────

function lerp(a, b, t) { return a + (b - a) * t; }

function drawNetwork(ctx, packets) {
  ctx.clearRect(0, 0, CANVAS_W, 240);
  ctx.fillStyle = B.surface;
  ctx.fillRect(0, 0, CANVAS_W, 240);

  // Edges
  EDGES.forEach(([a, b]) => {
    const na = NODES[a], nb = NODES[b];
    ctx.beginPath();
    ctx.moveTo(na.x, na.y);
    ctx.lineTo(nb.x, nb.y);
    ctx.strokeStyle = 'rgba(60,45,30,0.18)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  });

  // Nodes
  Object.entries(NODES).forEach(([, n]) => {
    ctx.save();
    if (n.role === 'endpoint') {
      ctx.beginPath();
      ctx.arc(n.x, n.y, 22, 0, Math.PI * 2);
      ctx.fillStyle = B.green;
      ctx.fill();
      ctx.strokeStyle = B.greenDark;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.font = `700 13px ${font}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(n.name[0], n.x, n.y);
      ctx.fillStyle = B.muted;
      ctx.font = `500 11px ${font}`;
      ctx.fillText(n.name, n.x, n.y + 32);
    } else if (n.role === 'switch') {
      const sw = 22, sh = 14;
      ctx.beginPath();
      ctx.roundRect(n.x - sw, n.y - sh / 2, sw * 2, sh, 3);
      ctx.fillStyle = B.charcoal;
      ctx.fill();
      ctx.strokeStyle = '#222';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.font = `500 9px ${font}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(n.label, n.x, n.y);
    } else {
      ctx.beginPath();
      ctx.arc(n.x, n.y, 17, 0, Math.PI * 2);
      ctx.fillStyle = B.blue;
      ctx.fill();
      ctx.strokeStyle = B.blueDark;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.font = `500 10px ${font}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(n.label, n.x, n.y);
    }
    ctx.restore();
  });

  // Packets
  packets.forEach(p => {
    if (!p.active) return;
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(p.px - 14, p.py - 10, 28, 20, 3);
    ctx.fillStyle = B.amber;
    ctx.fill();
    ctx.strokeStyle = B.amberDark;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = B.text;
    ctx.font = `700 9px ${font}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('P' + p.seq, p.px, p.py);
    ctx.restore();
  });
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PacketSwitchingSimulator({ onBack }) {
  const canvasRef      = useRef(null);
  const animRef        = useRef(null);
  const packetsRef     = useRef([]);
  const deliveredRef   = useRef(0);
  const totalRef       = useRef(0);
  const arrivalRankRef = useRef(0); // increments each time a packet lands

  const [msgIndex,      setMsgIndex]      = useState(0);
  const [speed,         setSpeed]         = useState(2);
  const [logItems,      setLogItems]      = useState([]);
  const [arrivedItems,  setArrivedItems]  = useState([]); // packets in arrival order
  const [assembled,     setAssembled]     = useState(null);
  const [expOpen,       setExpOpen]       = useState(false);
  const [running,       setRunning]       = useState(false);

  // Draw the idle network on mount and whenever not animating
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width  = CANVAS_W;
    canvas.height = 240;
    drawNetwork(canvas.getContext('2d'), []);
  }, []);

  const stopAnim = useCallback(() => {
    if (animRef.current) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }
  }, []);

  const tick = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pkts = packetsRef.current;

    let anyActive = false;
    pkts.forEach(p => {
      if (!p.active) return;
      if (p.segIdx >= p.route.length - 1) { p.active = false; return; }
      anyActive = true;
      p.t += p.spd;
      if (p.t >= 1) {
        p.t = 0;
        p.segIdx++;
        if (p.segIdx >= p.route.length - 1) {
          p.active = false;
          deliveredRef.current++;
          arrivalRankRef.current++;
          const rank = arrivalRankRef.current;
          setLogItems(prev => prev.map(it =>
            it.seq === p.seq ? { ...it, status: 'delivered', arrivalRank: rank } : it
          ));
          setArrivedItems(prev => [...prev, { seq: p.seq, data: p.data, rank }]);
          if (deliveredRef.current >= totalRef.current) {
            setAssembled(MESSAGES[p.msgIndex ?? 0]);
            setRunning(false);
          }
          return;
        }
      }
      const from = NODES[p.route[p.segIdx]];
      const to   = NODES[p.route[p.segIdx + 1]];
      p.px = lerp(from.x, to.x, p.t);
      p.py = lerp(from.y, to.y, p.t);
    });

    drawNetwork(ctx, pkts);
    if (anyActive) {
      animRef.current = requestAnimationFrame(tick);
    } else {
      animRef.current = null;
    }
  }, []);

  const handleSend = useCallback(() => {
    stopAnim();
    setAssembled(null);
    setArrivedItems([]);

    const msg  = MESSAGES[msgIndex];
    const baseSpd = speed * 0.007;
    const chunks = [];
    for (let i = 0; i < msg.length; i += PKT_SIZE) chunks.push(msg.slice(i, i + PKT_SIZE));

    const pkts = chunks.map((chunk, i) => {
      const routeIdx = i % ROUTES.length;
      const route = ROUTES[routeIdx];
      const from  = NODES[route[0]];
      return {
        seq: i + 1, data: chunk, route,
        segIdx: 0, t: 0,
        px: from.x, py: from.y,
        spd: baseSpd * ROUTE_SPEED_MULT[routeIdx],
        active: false,
        msgIndex,
      };
    });

    packetsRef.current   = pkts;
    deliveredRef.current = 0;
    arrivalRankRef.current = 0;
    totalRef.current     = pkts.length;

    setLogItems(pkts.map(p => ({
      seq:    p.seq,
      data:   p.data,
      route:  p.route.join(' → '),
      status: 'queued',
    })));
    setRunning(true);

    // Stagger packet launches
    pkts.forEach((p, i) => {
      setTimeout(() => {
        p.active = true;
        setLogItems(prev => prev.map(it =>
          it.seq === p.seq ? { ...it, status: 'transit' } : it
        ));
        if (!animRef.current) {
          animRef.current = requestAnimationFrame(tick);
        }
      }, i * Math.round(700 / speed));
    });
  }, [msgIndex, speed, stopAnim, tick]);

  const handleReset = useCallback(() => {
    stopAnim();
    packetsRef.current   = [];
    deliveredRef.current = 0;
    arrivalRankRef.current = 0;
    totalRef.current     = 0;
    setLogItems([]);
    setArrivedItems([]);
    setAssembled(null);
    setRunning(false);
    const canvas = canvasRef.current;
    if (canvas) drawNetwork(canvas.getContext('2d'), []);
  }, [stopAnim]);

  useEffect(() => () => stopAnim(), [stopAnim]);

  // ── Styles ──────────────────────────────────────────────────────────────────

  const s = {
    wrap: {
      fontFamily: font,
      background: B.bg,
      minHeight: '100vh',
      WebkitFontSmoothing: 'antialiased',
    },
    topbar: {
      position: 'sticky', top: 0, zIndex: 100,
      background: B.surface,
      borderBottom: `1px solid ${B.border}`,
      display: 'flex', alignItems: 'center',
      padding: '0 1.5rem', height: 60, gap: '1rem',
    },
    backBtn: {
      background: 'none', border: 'none', cursor: 'pointer',
      color: B.muted, fontFamily: font, fontSize: '0.85rem',
      fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5,
      padding: '4px 8px', borderRadius: 6,
    },
    title: {
      fontSize: '1rem', fontWeight: 700, color: B.text, margin: 0,
    },
    badge: {
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em',
      background: B.greenSoft, color: B.greenDark,
      padding: '3px 8px', borderRadius: 6,
    },
    slBadge: {
      fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em',
      background: '#e8eefb', color: '#4a63b0', padding: '3px 7px', borderRadius: 6,
    },
    hlBadge: {
      fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em',
      background: '#efe7fb', color: '#7a5bbf', padding: '3px 7px', borderRadius: 6,
    },
    body: {
      maxWidth: 760, margin: '0 auto', padding: '2rem 1.5rem 3rem',
    },
    card: {
      background: B.surface,
      border: `1px solid ${B.border}`,
      borderRadius: 14, padding: '1.25rem',
      marginBottom: '1rem',
    },
    sectionLabel: {
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em',
      color: B.faint, textTransform: 'uppercase', marginBottom: '0.6rem',
    },
    controlRow: {
      display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end',
    },
    ctrlGroup: {
      display: 'flex', flexDirection: 'column', gap: 4,
    },
    ctrlLabel: {
      fontSize: '0.72rem', fontWeight: 700, color: B.muted,
      textTransform: 'uppercase', letterSpacing: '0.04em',
    },
    select: {
      fontFamily: font, fontSize: '0.88rem',
      border: `1.5px solid ${B.borderStrong}`,
      borderRadius: 8, padding: '6px 10px',
      background: B.bg, color: B.text, cursor: 'pointer',
    },
    rangeHint: {
      fontSize: '0.72rem', color: B.faint,
    },
    btnPrimary: {
      background: B.green, color: '#fff', border: 'none',
      borderRadius: 8, padding: '8px 18px',
      fontSize: '0.88rem', fontWeight: 700,
      cursor: 'pointer', fontFamily: font,
    },
    btnGhost: {
      background: 'none', color: B.charcoal,
      border: `1.5px solid ${B.borderStrong}`,
      borderRadius: 8, padding: '7px 16px',
      fontSize: '0.88rem', fontWeight: 600,
      cursor: 'pointer', fontFamily: font,
    },
    canvas: {
      display: 'block', width: '100%', height: 240,
      borderRadius: 8,
    },
    legendRow: {
      display: 'flex', gap: '1rem', flexWrap: 'wrap',
      marginTop: '0.6rem',
    },
    legendItem: {
      display: 'flex', alignItems: 'center', gap: 5,
      fontSize: '0.72rem', color: B.muted,
    },
    logEntry: (status) => ({
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '5px 8px', borderRadius: 6,
      background: B.bg, marginBottom: 4,
      fontSize: '0.8rem',
    }),
    logBadge: {
      width: 20, height: 20, borderRadius: '50%',
      background: B.green, color: '#fff',
      fontSize: '0.65rem', fontWeight: 700,
      display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center', flexShrink: 0,
    },
    statusPill: (status) => ({
      fontSize: '0.7rem', fontWeight: 700,
      padding: '2px 8px', borderRadius: 10,
      flexShrink: 0,
      ...(status === 'queued'    ? { background: '#fef3e2', color: '#b97a0a' } :
          status === 'transit'   ? { background: B.greenSoft, color: B.greenDark } :
          status === 'delivered' ? { background: '#e8f4ec', color: B.charcoal } : {}),
    }),
    assembledBar: {
      display: 'flex', alignItems: 'center', gap: 8,
      marginTop: '0.75rem', padding: '8px 12px',
      background: B.greenSoft,
      borderRadius: 8, borderLeft: `3px solid ${B.green}`,
      fontSize: '0.82rem', color: B.charcoal,
    },
    expBtn: {
      width: '100%', display: 'flex', alignItems: 'center',
      justifyContent: 'space-between',
      background: 'none', border: 'none', cursor: 'pointer',
      fontFamily: font, fontSize: '0.9rem', fontWeight: 600,
      color: B.text, padding: '0.9rem 1.25rem',
      borderRadius: 14,
    },
    expBody: {
      padding: '0 1.25rem 1.25rem',
      borderTop: `1px solid ${B.border}`,
    },
    expGrid: {
      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem',
      marginTop: '0.9rem',
    },
    expCard: {
      padding: '0.9rem', borderRadius: 8,
      border: `1px solid ${B.border}`,
    },
    expCardTitle: {
      fontSize: '0.85rem', fontWeight: 700, color: B.text,
      marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: 7,
    },
    expList: {
      fontSize: '0.8rem', color: B.muted, lineHeight: 1.65,
      paddingLeft: '1rem', margin: 0,
    },
    expNote: {
      fontSize: '0.78rem', color: B.faint,
      lineHeight: 1.6, marginTop: '0.75rem',
    },
  };

  const dotSwitch = { width: 12, height: 12, borderRadius: 2, background: B.charcoal, flexShrink: 0 };
  const dotRouter = { width: 12, height: 12, borderRadius: '50%', background: B.blue, flexShrink: 0 };
  const dotEndpt  = { width: 12, height: 12, borderRadius: '50%', background: B.green, flexShrink: 0 };
  const dotPkt    = { width: 16, height: 10, borderRadius: 2, background: B.amber, flexShrink: 0 };

  return (
    <div style={s.wrap}>

      {/* ── Topbar ─────────────────────────────────────────────────────── */}
      <header style={s.topbar}>
        {onBack && (
          <button style={s.backBtn} onClick={onBack} aria-label="Back">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <polyline points="10 3 5 8 10 13" />
            </svg>
            Back
          </button>
        )}
        <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
          <img src="/logo.svg" alt="brookebit" height={40} style={{ display: 'block' }} />
        </a>
        <h1 style={s.title}>Packet Switching</h1>
        <span style={s.badge}>A2.3.3</span>
        <span style={s.slBadge}>SL</span>
        <span style={s.hlBadge}>HL</span>
      </header>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <main style={s.body}>

        {/* ── Controls ───────────────────────────────────────────────── */}
        <div style={s.card}>
          <div style={s.sectionLabel}>Configuration</div>
          <div style={s.controlRow}>
            <div style={s.ctrlGroup}>
              <label style={s.ctrlLabel}>Message</label>
              <select
                style={s.select}
                value={msgIndex}
                onChange={e => setMsgIndex(+e.target.value)}
                disabled={running}
              >
                {MESSAGES.map((m, i) => (
                  <option key={m} value={i}>{m}</option>
                ))}
              </select>
            </div>
            <div style={s.ctrlGroup}>
              <label style={s.ctrlLabel}>Animation speed</label>
              <input
                type="range" min="1" max="3" step="1"
                value={speed}
                onChange={e => setSpeed(+e.target.value)}
                disabled={running}
                style={{ accentColor: B.green, width: 100 }}
              />
              <span style={s.rangeHint}>{speed === 1 ? 'Slow' : speed === 2 ? 'Medium' : 'Fast'}</span>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <button
                style={{ ...s.btnPrimary, opacity: running ? 0.6 : 1 }}
                onClick={handleSend}
                disabled={running}
              >
                ▶ Send
              </button>
              <button style={s.btnGhost} onClick={handleReset}>Reset</button>
            </div>
          </div>
          <p style={{ fontSize: '0.72rem', color: B.faint, marginTop: '0.75rem', lineHeight: 1.5 }}>
            Each packet carries a fixed-size payload. In real networks, payload size is set by the network's
            MTU (Maximum Transmission Unit), typically 1,500 bytes for Ethernet, not chosen per message.
          </p>
        </div>

        {/* ── Canvas ─────────────────────────────────────────────────── */}
        <div style={s.card}>
          <div style={s.sectionLabel}>Network topology</div>
          <canvas
            ref={canvasRef}
            style={s.canvas}
          />
          <div style={s.legendRow}>
            <div style={s.legendItem}><span style={dotEndpt} />Sender / Receiver</div>
            <div style={s.legendItem}><span style={dotSwitch} />Switch (LAN)</div>
            <div style={s.legendItem}><span style={dotRouter} />Router (WAN)</div>
            <div style={s.legendItem}><span style={dotPkt} />Packet in transit</div>
          </div>
        </div>

        {/* ── Packet log ─────────────────────────────────────────────── */}
        <div style={s.card}>
          <div style={s.sectionLabel}>Packet log</div>

          {logItems.length === 0 ? (
            <div style={{ fontSize: '0.82rem', color: B.faint, padding: '4px 0' }}>
              Press Send to begin simulation.
            </div>
          ) : (
            <>
              {logItems.map(it => (
                <div key={it.seq} style={s.logEntry(it.status)}>
                  <span style={s.logBadge}>{it.seq}</span>
                  <span style={{ fontWeight: 700, color: B.text, minWidth: 42 }}>
                    &ldquo;{it.data}&rdquo;
                  </span>
                  <span style={{ color: B.faint, fontSize: '0.75rem', fontFamily: mono, flex: 1 }}>
                    {it.route}
                  </span>
                  <span style={s.statusPill(it.status)}>
                    {it.status === 'queued' ? 'queued' :
                     it.status === 'transit' ? 'in transit' : 'delivered'}
                  </span>
                </div>
              ))}

              {assembled && (() => {
                const inArrivalOrder = [...arrivedItems].sort((a, b) => a.rank - b.rank);
                const inSeqOrder     = [...arrivedItems].sort((a, b) => a.seq  - b.seq);
                const isOutOfOrder   = inArrivalOrder.some((p, i) => p.seq !== inSeqOrder[i].seq);
                const pktBox = (p, highlight) => (
                  <span key={p.seq} style={{
                    display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                    background: highlight ? B.greenSoft : '#fef9ee',
                    border: `1.5px solid ${highlight ? B.green : B.amberDark}`,
                    borderRadius: 6, padding: '3px 8px', minWidth: 36,
                    fontSize: '0.72rem', fontFamily: mono,
                  }}>
                    <span style={{ color: B.faint, fontSize: '0.62rem' }}>P{p.seq}</span>
                    <span style={{ fontWeight: 700, color: B.text }}>&ldquo;{p.data}&rdquo;</span>
                  </span>
                );
                return (
                  <div style={{ marginTop: '0.75rem', borderTop: `1px solid ${B.border}`, paddingTop: '0.75rem' }}>
                    {isOutOfOrder && (
                      <div style={{ marginBottom: '0.5rem' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: B.faint, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>
                          Arrived at destination (out of order)
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          {inArrivalOrder.map((p, i) => (
                            <React.Fragment key={p.seq}>
                              {pktBox(p, false)}
                              {i < inArrivalOrder.length - 1 && (
                                <span style={{ color: B.faint, fontSize: '0.8rem' }}>→</span>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: B.faint, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>
                        Reordered by sequence number
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        {inSeqOrder.map((p, i) => (
                          <React.Fragment key={p.seq}>
                            {pktBox(p, true)}
                            {i < inSeqOrder.length - 1 && (
                              <span style={{ color: B.faint, fontSize: '0.8rem' }}>+</span>
                            )}
                          </React.Fragment>
                        ))}
                        <span style={{ color: B.faint, fontSize: '0.9rem', margin: '0 2px' }}>=</span>
                        <span style={{
                          background: B.green, color: '#fff',
                          borderRadius: 6, padding: '4px 12px',
                          fontWeight: 700, fontSize: '0.88rem', fontFamily: mono,
                          letterSpacing: '0.04em',
                        }}>{assembled}</span>
                      </div>
                    </div>
                    {isOutOfOrder && (
                      <p style={{ fontSize: '0.75rem', color: B.faint, marginTop: '0.5rem', lineHeight: 1.5 }}>
                        Packets arrived out of sequence because each took a different route. The sequence number in each packet header allowed the destination to reorder them correctly.
                      </p>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>

        {/* ── Explainer accordion ─────────────────────────────────────── */}
        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <button
            style={s.expBtn}
            onClick={() => setExpOpen(o => !o)}
            aria-expanded={expOpen}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none"
                stroke={B.green} strokeWidth="1.8" strokeLinecap="round"
                strokeLinejoin="round" aria-hidden="true">
                <circle cx="8" cy="8" r="6.5" />
                <line x1="8" y1="7" x2="8" y2="11" />
                <circle cx="8" cy="5" r="0.5" fill={B.green} stroke="none" />
              </svg>
              Role of switches and routers in packet switching
            </span>
            <svg
              width="14" height="14" viewBox="0 0 16 16" fill="none"
              stroke={B.green} strokeWidth="2" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true"
              style={{ transform: expOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
            >
              <polyline points="3 6 8 11 13 6" />
            </svg>
          </button>

          {expOpen && (
            <div style={s.expBody}>
              <div style={s.expGrid}>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>
                    <span style={dotSwitch} />
                    Switch
                  </div>
                  <ul style={s.expList}>
                    <li>Operates at <strong>Layer 2</strong> (Data Link)</li>
                    <li>Connects devices <strong>within a single LAN</strong></li>
                    <li>Forwards frames using <strong>MAC addresses</strong></li>
                    <li>Maintains a MAC address table (no routing decisions)</li>
                    <li>Fast; does not read IP headers</li>
                  </ul>
                </div>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>
                    <span style={dotRouter} />
                    Router
                  </div>
                  <ul style={s.expList}>
                    <li>Operates at <strong>Layer 3</strong> (Network)</li>
                    <li>Connects <strong>different networks</strong> (LAN to WAN)</li>
                    <li>Forwards packets using <strong>IP addresses</strong></li>
                    <li>Consults a routing table to choose the best path</li>
                    <li>Enables packets to travel independent routes</li>
                  </ul>
                </div>
              </div>
              <p style={s.expNote}>
                In this simulation the sender and receiver each connect through a <strong>switch</strong> within
                their local LAN. The switch forwards frames to the local router by MAC address. Packets then
                travel independently across the <strong>router mesh</strong>, each taking whichever path the
                router's table selects, before being reassembled in sequence at the destination.
              </p>
            </div>
          )}
        </div>

      </main>
    </div>
  );
}
